import { useForm } from "react-hook-form";
import AppBar from "../../components/atoms/appbar";
import Button from "../../components/atoms/button";
import Input from "../../components/atoms/input";
import type { CommnuityInput } from "../../types/community.types";
import { zodResolver } from "@hookform/resolvers/zod";
import { communitySchema } from "../../lib/schemas/community.schema";
import * as React from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  communityArticleEdit,
  communityArticleWrite,
} from "../../@apis/v1/community.service";
import { useCommnuityArticleDetail } from "../../@apis/hooks/useCommunity";
import { useParams, useSearchParams } from "react-router-dom";
import { useMainGolfParkDetail } from "../../@apis/hooks/useMain";
import { cn } from "../../lib/utils/cn";
import { vw } from "../../lib/utils/responsiveUtils";
import { staticURL } from "../../lib/utils/imageUtils";
import { useModalStore } from "../../lib/store/useModalStore";
import type { GolfParkSearchResponse } from "../../@apis/dto/response/main.response";
import { useRouter } from "../../hooks/useRouter";
import type { OnErrorResponse } from "../../@apis/dto/response/common.response";
import ImageResizerUploader from "../../lib/file/resizer";

const MAX_FILES = 4; // 최대 파일 개수

export default function CommunityFormPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname, push, back } = useRouter();
  const { openModal } = useModalStore();
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const _type = pathname.includes("edit") ? "edit" : "add";
  const _store = searchParams.get("store");

  const { data: commnuityData } = useQuery({
    ...useCommnuityArticleDetail(id as string),
    enabled: !!id,
  });
  const { article } = commnuityData?.data || {};

  const { data: storeData, isSuccess: storeSuccess } = useQuery({
    ...useMainGolfParkDetail(_store || ""),
    enabled: !!_store,
  });
  const { golfPark } = storeData?.data || {};

  // STATE
  const [files, setFiles] = React.useState<File[]>([]);
  const [isResizing, setIsResizing] = React.useState(false);
  const [footerHeight, setFooterHeight] = React.useState(0);
  const footerRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const el = footerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      setFooterHeight(el.offsetHeight);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const { register, handleSubmit, watch, reset } = useForm<CommnuityInput>({
    resolver: zodResolver(communitySchema),
    defaultValues: {
      title: article?.title || "",
      content: article?.content || "",
    },
  });

  // HANDLER
  const openPicker = () => {
    if (files?.length === 4)
      return openModal("ALERT_DIALOG", {
        title: "첨부",
        content: "최대 4개까지 첨부할 수 있습니다.",
      });
    inputRef.current?.click();
  };

  const changeHandler = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files: selectedFiles } = e.target;

    if (selectedFiles) {
      if (files.length + selectedFiles.length > MAX_FILES) {
        openModal("ALERT_DIALOG", {
          title: "첨부",
          content: `이미지는 최대 ${MAX_FILES}개까지 업로드할 수 있습니다.`,
        });
        e.target.value = "";
        return;
      }

      setIsResizing(true);
      try {
        const resizedFiles = await ImageResizerUploader(selectedFiles);
        if (resizedFiles) {
          setFiles((prev) => [...prev, ...resizedFiles]);
        }
      } catch (error) {
        console.error("Image resizing failed:", error);
      } finally {
        setIsResizing(false);
        e.target.value = "";
      }
    }
  };

  const onSubmit = (form: CommnuityInput) => {
    if (_type === "edit") {
      communityEditMutation.mutate({
        form: { ...form, golfParkCode: _store || "", articleId: id as string },
        file: files,
      });
    } else {
      commnuityAddMutation.mutate({
        form: { ...form, golfParkCode: _store || "" },
        file: files,
      });
    }
  };

  const commnuityAddMutation = useMutation({
    mutationFn: ({
      form,
      file,
    }: {
      form: CommnuityInput & { golfParkCode: string | number };
      file: File[];
    }) => communityArticleWrite(form, file),
    onSuccess: (res) => {
      const { success, error, data } = res;
      if (success) {
        // 목록 캐시는 그대로 두고(스크롤/페이지 복원용), 돌아왔을 때 제자리 갱신하도록 플래그만 남긴다.
        sessionStorage.setItem("community_needs_refresh", "true");
        push(`/community/${data}`, { replace: true });
      } else {
        openModal("ALERT_DIALOG", {
          title: "에러",
          content:
            error?.code === "C003" ? "요청에 실패하였습니다." : error?.message,
        });
      }
    },
    onError: (err: OnErrorResponse) => {
      const { error } = err.response.data;
      openModal("ALERT_DIALOG", {
        title: "에러",
        content:
          error?.code === "C003" ? "요청에 실패하였습니다." : error?.message,
      });
    },
  });

  const communityEditMutation = useMutation({
    mutationFn: ({
      form,
      file,
    }: {
      form: CommnuityInput & {
        golfParkCode: string | number;
        articleId: string | number;
      };
      file: File[];
    }) => communityArticleEdit(form, file),
    onSuccess: (res) => {
      const { success, error } = res;
      if (success) {
        // 목록 캐시는 유지하고(스크롤 복원용) 돌아왔을 때 제자리 갱신하도록 플래그만 남긴다.
        // 상세는 refetchOnMount 기본값(true)이라 진입 시 자동 최신화된다.
        sessionStorage.setItem("community_needs_refresh", "true");
        push(`/community/${id}`, { replace: true });
      } else {
        alert(error?.message);
      }
    },
    onError: (err: OnErrorResponse) => {
      const { error } = err.response.data;
      openModal("ALERT_DIALOG", {
        title: "에러",
        content:
          error?.code === "C003" ? "요청에 실패하였습니다." : error?.message,
      });
    },
  });

  async function urlToFile(url: string): Promise<File> {
    const response = await fetch(url);
    const blob = await response.blob();
    const afterLastSlash = url.split("/").pop() || "";
    const parts = afterLastSlash.split("_", 2);
    return new File([blob], parts[1], { type: blob.type });
  }

  React.useEffect(() => {
    if (_type === "edit") {
      (article?.fileList || []).map(async ({ fileUrl }) => {
        const file = await urlToFile(staticURL(fileUrl));
        setFiles((prev) => [...prev, file]);
      });
    }
  }, [_type, article]);

  React.useEffect(() => {
    // article 데이터가 성공적으로 로드되었을 때
    if (article) {
      // reset 함수를 사용해 폼의 값을 article 데이터로 업데이트합니다.
      reset({
        title: article.title,
        content: article.content,
      });
    }
  }, [article, reset]);

  return (
    <div className="relative">
      <AppBar title="게시물 작성" onBack={() => back()} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <section className="px-[15px]" style={{ paddingBottom: footerHeight }}>
          <Button
            className="w-full border border-[#e6e6e6]"
            label=""
            type="button"
            onClick={() =>
              openModal("SELECT_GOLFPARKS_MODAL", {
                onClick: (item: GolfParkSearchResponse) =>
                  // replace로 history를 쌓지 않아 뒤로가기 시 한 단계로 복귀 가능
                  setSearchParams({ store: item.code }, { replace: true }),
              })
            }
          >
            <div className="flex-row gap-[15px]">
              <img className="size-4" src="/icons/ic-flag.webp" />
              <p>골프장 선택</p>
            </div>
          </Button>

          {storeSuccess && (
            <div className="bg-[#f5f5f5] px-[20px] py-[14px] rounded-[15px] gap-x-[20px] mb-[15px] flex mt-[10px]">
              {golfPark?.imageUrl ? (
                <img
                  src={staticURL(golfPark.imageUrl)}
                  alt="_blank"
                  className="rounded-[10px] shrink-0 mt-[6px]"
                  style={{ width: vw(83), height: vw(83) }}
                />
              ) : (
                <img
                  src="/images/img-login-logo.webp"
                  alt="_blank"
                  className="rounded-[10px] shrink-0 mt-[6px]"
                  style={{ width: vw(83), height: vw(83) }}
                />
              )}
              <div>
                <p className="font-bold text-2xl">{golfPark?.name}</p>
                <div className="flex gap-[5px] items-start mt-[14px] font-[500] text-[#888] text-sm ">
                  <img
                    src="/icons/ic-place-gray.webp"
                    alt="place"
                    className="size-[0.9rem] mt-[4px]"
                  />
                  <p className="whitespace-pre-line break-keep break-words">
                    {golfPark?.address}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="mt-[25px] space-y-[20px]">
            <div className="flex-row-between">
              <p className="font-bold">제목</p>
              <div className="text-sm font-[500] text-[#888]">
                <span className="text-[#e5002b]">{watch("title").length}</span>
                <span>/30</span>
              </div>
            </div>
            <Input
              placeholder="제목을 입력해주세요."
              className="text-[0.7rem] placeholder:text-[0.7rem]"
              {...register("title")}
              maxLength={30}
            />
          </div>

          <div className="mt-[25px] space-y-[20px]">
            <div className="flex-row-between">
              <p className="font-bold">내용</p>
              <div className="ext-sm font-[500] text-[#888]">
                <span className="text-[#e5002b]">
                  {watch("content").length}
                </span>
                <span>/500</span>
              </div>
            </div>

            <textarea
              className="text-[0.7rem] placeholder:text-[0.7rem] h-[200px] p-[18px] border border-[#e6e6e6] w-full rounded-[10px]"
              placeholder="내용을 입력해주세요."
              maxLength={500}
              {...register("content")}
            />
          </div>
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            accept="image/*"
            multiple
            onChange={changeHandler}
          />
          <Button
            className="w-full "
            type="button"
            variant={"black"}
            label=""
            onClick={openPicker}
          >
            <div className="flex-row-between w-full text-[#888] text-[0.6rem]">
              <div className="w-[47px]" />
              <div>
                <span className="text-white text-sm font-bold">
                  {isResizing
                    ? "이미지 처리 중..."
                    : files.length > 0
                      ? `${files.length}개 첨부`
                      : "사진 첨부"}
                </span>
                {!isResizing && <span className="text-sm">(선택)</span>}
              </div>
              <span>최대 4개</span>
            </div>
          </Button>

          {files.length > 0 && (
            <div className="flex-row flex-wrap gap-[10px] mt-5">
              {files.map((item, index) => (
                <div
                  key={index}
                  className="relative border border-[#e6e6e6] rounded-[15px] w-[calc(50%_-_5px)]"
                >
                  <img
                    className="object-cover rounded-[15px] w-full"
                    style={{
                      height: vw(167),
                    }}
                    src={URL.createObjectURL(item)}
                  />
                  <div
                    className="absolute top-[10px] right-[10px] cursor-pointer bg-[#888] rounded-full flex-row justify-center"
                    style={{
                      width: vw(30),
                      height: vw(30),
                    }}
                    onClick={() =>
                      setFiles(files.filter((_, i) => i !== index))
                    }
                  >
                    <img
                      src="/icons/ic-close-white.webp"
                      style={{
                        width: vw(10),
                        height: vw(10),
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <div
          ref={footerRef}
          className={cn(
            "bg-white p-[15px] fixed max-w-[440px] w-full bottom-0 flex-row-between gap-[10px] z-50",
          )}
        >
          <Button
            type="button"
            className="w-full font-bold"
            variant={"white"}
            label="취소"
            onClick={() => back()}
          />
          <Button
            type="submit"
            disabled={isResizing}
            className="w-full font-bold"
            variant={_type === "edit" ? "black" : "red"}
            label={_type === "edit" ? "수정 완료" : "등록하기"}
          />
        </div>
      </form>
    </div>
  );
}
